import { Helmet } from "react-helmet-async";
import Layout from "../../components/Layout";

// ✅ Custom video background wrapper
function BackgroundWrapper({ children }) {
  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background video */}
      <video
        className="fixed inset-0 z-0 w-full h-full object-cover"
        src="/assets/bg1.mp4"
        autoPlay
        muted
        loop
        playsInline
      />

      {/* Dark overlay */}
      <div className="fixed inset-0 z-0 bg-black/60" />

      {/* Page content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}

export default function YouTubePage() {
  const creators = [
    {
      name: "Chaotik Official",
      url: "https://www.youtube.com/@ChaotikorgSC",
      avatar:
        "https://yt3.googleusercontent.com/2MvoJ5jVcyvcFR6xkS3fv_Eufc3Ke1If4dOB-AG8SeiotIbp454ezE1wt08aDQBHCHei_P8R=s160-c-k-c0x00ffffff-no-rj",
      description: "Gameplay and highlights from the Chaotik community.",
    },
    {
      name: "SC-Vinchenz",
      url: "https://www.youtube.com/@SC-Vinchenz-lh9gi",
      avatar:
        "https://yt3.googleusercontent.com/HHcGkMbsKvZnHLG3yrVliEzxBwP5--SvpYxKEpBGT1h9gDz2QgegXwWPZjNwdAWg-wIFshm90S8=s160-c-k-c0x00ffffff-no-rj",
      description: "Star Citizen videos, tactics, and gameplay by Vinchenz.",
    },
    {
      name: "Qravey",
      url: "https://www.youtube.com/@qravey",
      avatar:
        "https://yt3.googleusercontent.com/izfXW7MA2X765iUIVXR6H5AUMGjaOHb6fI7heenNG0xLadCsTf-4-_dEqN_ua74BmvdwF1Ua=s160-c-k-c0x00ffffff-no-rj",
      description: "Star Citizen gameplay and highlights from Qravey.",
    },
  ];

  const columnCount = Math.min(creators.length, 3) || 1;
  const rowCount = Math.max(1, Math.ceil(creators.length / columnCount));

  const widthByColumns = {
    1: "max-w-xl",
    2: "max-w-4xl",
    3: "max-w-6xl",
  };

  const gridColumnsByCount = {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
  };

  const gridWidthClass = widthByColumns[columnCount] ?? "max-w-6xl";
  const responsiveColumns = gridColumnsByCount[columnCount] ?? "sm:grid-cols-2 lg:grid-cols-3";
  const autoRowClass = rowCount > 1 ? "auto-rows-fr" : "";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://chaotik.org/" },
      { "@type": "ListItem", position: 2, name: "YouTube", item: "https://chaotik.org/media/youtube" },
    ],
  };

  // Only include creators with verified channel URLs in sameAs JSON-LD.
  const verifiedCreators = creators.filter((c) =>
    c.url && c.url.startsWith("https://www.youtube.com/@")
  );

  const personJsonLd = verifiedCreators.map((c) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    name: c.name,
    url: c.url,
    sameAs: [c.url],
    memberOf: { "@type": "Organization", name: "CHAOTIK", url: "https://chaotik.org/" },
  }));

  return (
    <BackgroundWrapper>
      <Layout>
        <Helmet>
          <title>CHAOTIK YouTube Creators | Star Citizen Org</title>
          <meta
            name="description"
            content="YouTube creators in CHAOTIK, a 24/7 multi-role Star Citizen organization. Star Citizen videos, tactics and gameplay highlights."
          />
          <link rel="canonical" href="https://chaotik.org/media/youtube" />
          <meta property="og:title" content="CHAOTIK YouTube Creators | Star Citizen Org" />
          <meta property="og:description" content="YouTube creators in CHAOTIK, a 24/7 multi-role Star Citizen organization." />
          <meta property="og:url" content="https://chaotik.org/media/youtube" />
          <meta name="twitter:title" content="CHAOTIK YouTube Creators" />
          <meta name="twitter:description" content="YouTube creators in CHAOTIK, a 24/7 multi-role Star Citizen organization." />
          <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
          {personJsonLd.map((j, idx) => (
            <script key={idx} type="application/ld+json">{JSON.stringify(j)}</script>
          ))}
        </Helmet>
        <div className="flex flex-col items-center text-center px-4 py-12">
          <h1 className="text-white text-4xl sm:text-5xl font-bebas mb-10">CHAOTIK YouTube Creators</h1>

          <div
            className={`grid grid-cols-1 ${responsiveColumns} ${autoRowClass} gap-8 w-full ${gridWidthClass} mx-auto justify-items-center`}
          >
            {creators.map((creator) => (
              <a
                key={creator.name}
                href={creator.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black/50 backdrop-blur-md p-6 rounded-xl hover:bg-black/70 transition-all text-white flex flex-col items-center text-center w-full h-full border border-red-500/40 shadow-[0_0_25px_rgba(248,113,113,0.35)]"
              >
                <img
                  src={creator.avatar}
                  alt={`${creator.name} - CHAOTIK Star Citizen YouTube creator avatar`}
                  className="w-24 h-24 rounded-full mb-4 border border-red-500/40 shadow-[0_0_20px_rgba(248,113,113,0.35)] object-cover"
                  loading="lazy"
                />
                <h3 className="text-xl sm:text-2xl font-bold">{creator.name}</h3>
                <p className="text-sm sm:text-base text-white/70 mt-2">{creator.description}</p>
              </a>
            ))}
          </div>
        </div>
      </Layout>
    </BackgroundWrapper>
  );
}
